import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from '@reach/router';
import { axisPaths } from '../AxisConstants';
import QuoteWizardState, {
  updateErrors,
} from '../../../store/QuoteWizardState';
import { hybridAnswers } from '../../util/Quote';
import {
  answeredFormMatchesQuoteContext,
  isQuoteSubmissionInProgress,
} from '../../QuoteWizard/utility';
import { useUsingAxisSettings } from '../../../hooks/useUsingAxisSettings';
import { useQuoteSubmission } from '../../../hooks/useQuoteSubmission';
import { CompleteQuoteSubmissionError } from '../../QuoteWizard/ErrorHandling';
import { QuoteSubmissionProgress } from '../../QuoteWizard/QuoteReview/QuoteSubmissionProgress/QuoteSubmissionProgress';
import { createApplicationFinishedThunk } from '../../../store/analyticsStore';
import { errorify } from '../../../util';
import type { StoreState } from '../../../store';
import { CoreTaskStatusChoices, GetTaskPoolQuery } from '../../../gql/graphql';
import { AxisSubmitAndExitFollowUpText } from './AxisSubmitAndExitFollowUpText';
import { QuoteStatus } from '../../../Typescript/backend/types/QuoteStatus';
import { QuoteWizardForm } from '../../QuoteWizard/classes';

const completedQuoteStatuses: Array<QuoteStatus> = [
  QuoteStatus.Complete,
  QuoteStatus.Renewed,
  QuoteStatus.Confirmed,
];

export const AxisSubmitAndExitButton = () => {
  const usingAxis = useUsingAxisSettings();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [submissionStarted, setSubmissionStarted] = useState(false);

  const visible = useSelector(
    (state: StoreState) => state.quoteWizard.saveButton.visible,
  );
  const reconcileState = useSelector(
    (state: StoreState) => state.quoteWizard.saveButton.reconcileState,
  );
  const answersByFormId = useSelector(
    (state: StoreState) => state.quoteWizard.formAnswers,
  );
  const selectedBusinessType = useSelector(
    (state: StoreState) => state.quoteWizard.selectedBusinessType,
  );
  const selectedPolicies = useSelector(
    (state: StoreState) => state.quoteWizard.selectedPolicies,
  );
  const businessForm = useSelector(
    (state: StoreState) => state.quoteWizard.businessForm,
  );
  const policyForms = useSelector(
    (state: StoreState) => state.quoteWizard.policyForms,
  );
  const quoteSubmissionTaskPoolId = useSelector(
    (state: StoreState) => state.quoteWizard.quoteSubmissionTaskPoolId,
  );
  const submittedQuotes = useSelector(
    (state: StoreState) => state.quoteWizard.submittedQuotes,
  );
  const currentQuoteUUID = useSelector(
    (state: StoreState) => state.quoteWizard.currentQuoteUUID,
  );
  const renewalInProgress = useSelector(
    (state: StoreState) => state.quoteWizard.renewalInProgress,
  );
  const confirmationInProgress = useSelector(
    (state: StoreState) => state.quoteWizard.confirmationInProgress,
  );

  const allSelectedPolicyIds = useMemo(
    () => selectedPolicies.map((fullPolicy) => fullPolicy.id),
    [selectedPolicies],
  );

  const formsWithAnswers = useMemo(
    () =>
      Object.values(answersByFormId).filter((answeredForm) =>
        answeredFormMatchesQuoteContext(
          answeredForm,
          selectedBusinessType,
          allSelectedPolicyIds,
        ),
      ),
    [answersByFormId, selectedBusinessType, allSelectedPolicyIds],
  );

  const allRelevantForms = useMemo(
    () =>
      [businessForm, ...policyForms].filter(
        (form): form is QuoteWizardForm =>
          QuoteWizardForm.isQuoteWizardForm(form) &&
          (!form.policy || allSelectedPolicyIds.includes(form.policy.id)),
      ),
    [businessForm, policyForms, allSelectedPolicyIds],
  );

  const submissionInProgress = isQuoteSubmissionInProgress(
    formsWithAnswers,
    quoteSubmissionTaskPoolId,
  );

  const quoteSubmitted =
    submittedQuotes.length > 0
      ? completedQuoteStatuses.includes(
          submittedQuotes[submittedQuotes.length - 1].status,
        )
      : false;

  const submitStatus: QuoteStatus = renewalInProgress
    ? QuoteStatus.Renewed
    : confirmationInProgress
    ? QuoteStatus.Confirmed
    : QuoteStatus.Complete;

  const onSubmitAndExit = useCallback(() => {
    const resolvedBusinessType = selectedBusinessType
      ? 'original' in selectedBusinessType
        ? selectedBusinessType.original
        : selectedBusinessType
      : undefined;

    dispatch(
      QuoteWizardState.actions.confirmAnswersForForms({
        businessType: resolvedBusinessType,
        formsWithAnswers: hybridAnswers(formsWithAnswers, allRelevantForms),
        status: submitStatus,
        currentQuoteUUID,
        renewalInProgress,
        selectedPolicies: [],
        signature: null,
        storeExistingQuoteUuidOnComplete: false,
      }),
    );
  }, [
    dispatch,
    selectedBusinessType,
    formsWithAnswers,
    allRelevantForms,
    submitStatus,
    currentQuoteUUID,
    renewalInProgress,
  ]);

  const { taskPool } = useQuoteSubmission({
    active: submissionStarted,
    onComplete: () => {
      dispatch(createApplicationFinishedThunk());
    },
  });

  const handleClick = useCallback(() => {
    reconcileState();
    setSubmissionStarted(true);
    onSubmitAndExit();
  }, [reconcileState, onSubmitAndExit]);

  useEffect(() => {
    if (!quoteSubmitted) {
      return;
    }

    setSubmissionStarted(false);
    navigate(axisPaths.thanks.url);
  }, [quoteSubmitted, navigate]);

  useEffect(() => {
    const taskPoolData = taskPool.data?.getTaskPool;
    const error = taskPool.error || taskPoolData?.error;

    if (!submissionStarted || !error) {
      return;
    }

    setSubmissionStarted(false);
    dispatch(
      updateErrors({
        completeQuoteSubmission: errorify(error),
      }),
    );
  }, [dispatch, submissionStarted, taskPool.data?.getTaskPool, taskPool.error]);

  if (!usingAxis || !visible) {
    return null;
  }

  const taskPoolData = taskPool.data?.getTaskPool;
  const hasTaskPoolErrors = !!taskPool.error || !!taskPoolData?.error;
  const progressPercentage = taskPoolData
    ? getProgressPercentage(taskPoolData)
    : 0;
  const isTaskPoolComplete = progressPercentage === 100;
  const isLoading = Boolean(
    submissionStarted &&
      (taskPool.loading ||
        (taskPoolData && !isTaskPoolComplete && !hasTaskPoolErrors)),
  );
  const shouldShowProgress =
    submissionStarted && taskPoolData && !hasTaskPoolErrors;

  return (
    <Outer>
      {shouldShowProgress ? (
        <QuoteSubmissionProgress taskPool={taskPoolData ?? null} />
      ) : (
        <>
          <AxisSubmitAndExitFollowUpText centred />
          <SubmitAndExitButton
            type='button'
            onClick={handleClick}
            disabled={submissionInProgress || isLoading}
          >
            {isLoading ? 'Submitting...' : 'Submit and Exit Application'}
          </SubmitAndExitButton>
        </>
      )}
      <CompleteQuoteSubmissionError />
    </Outer>
  );
};

function getProgressPercentage(
  taskPool: NonNullable<GetTaskPoolQuery['getTaskPool']> | null | undefined,
): number {
  if (!taskPool?.tasks) {
    return 0;
  }

  const tasks = taskPool.tasks.filter(
    (task): task is NonNullable<typeof task> => task != null,
  );

  if (tasks.length === 0) {
    return 0;
  }

  const finishedTasks = tasks.filter(
    (task) =>
      task.status === CoreTaskStatusChoices.Completed ||
      task.status === CoreTaskStatusChoices.Failed,
  ).length;

  return (finishedTasks / tasks.length) * 100;
}

const Outer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  marginTop: theme.spacing(2),
  gap: theme.spacing(2),
}));

const SubmitAndExitButton = styled(Button)(({ theme }) => ({
  ...theme.typography.button,
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'transparent',
  padding: `${theme.spacing(0.75)} ${theme.spacing(2.5)}`,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
  '&:disabled': {
    color: theme.palette.action.disabled,
    borderColor: theme.palette.action.disabledBackground,
  },
}));
